// Puente entre los interceptores de axios y el arbol de React. Un interceptor
// no es un componente, asi que no puede despachar sobre AuthContext: avisa por
// aca y `client.jsx` traduce ese aviso en un dispatch de logout.
// Tambien concentra la lectura/escritura del storage, que antes estaba repartida
// entre `useLogin`, `useAuthentication` y el interceptor de request.

const TOKEN_KEY = 'sessionId';
const LOGGED_IN_KEY = 'isLoggedIn';
const RETURN_TO_KEY = 'returnTo';
const EXPIRED_KEY = 'sessionExpired';

let listeners = [];

function local() {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

function session() {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    return window.sessionStorage;
  }
  return null;
}

function getToken() {
  const storage = local();
  return (storage && storage.getItem(TOKEN_KEY)) || '';
}

function getIsLoggedIn() {
  const storage = local();
  return !!storage && storage.getItem(LOGGED_IN_KEY) === 'true';
}

function persistSession(token) {
  const storage = local();
  if (!storage) return;
  storage.setItem(TOKEN_KEY, token);
  storage.setItem(LOGGED_IN_KEY, 'true');
}

function clearSession() {
  const storage = local();
  if (!storage) return;
  storage.removeItem(TOKEN_KEY);
  storage.removeItem(LOGGED_IN_KEY);
}

// Guarda la ruta en la que estaba el usuario cuando la sesion vencio, para
// devolverlo ahi despues del re-login. Vive en sessionStorage: es por pestaña
// y no debe sobrevivir al cierre del navegador.
function rememberReturnTo() {
  const storage = session();
  if (!storage || typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  if (!pathname || pathname === '/login' || pathname === '/') return;
  storage.setItem(RETURN_TO_KEY, `${pathname}${search || ''}`);
}

// Devuelve la ruta guardada y la consume, para no reusarla en un login posterior.
function takeReturnTo() {
  const storage = session();
  if (!storage) return '';
  const path = storage.getItem(RETURN_TO_KEY) || '';
  storage.removeItem(RETURN_TO_KEY);
  return path;
}

// El aviso viaja por sessionStorage y no por el MessageProvider: al montar,
// `useLogin` hace un reset de mensajes que se comeria el aviso justo despues
// del redirect. Asi ademas sobrevive a un refresh de la pagina.
function takeExpiredNotice() {
  const storage = session();
  if (!storage) return false;
  const notice = storage.getItem(EXPIRED_KEY) === 'true';
  storage.removeItem(EXPIRED_KEY);
  return notice;
}

function markExpiredNotice() {
  const storage = session();
  if (!storage) return;
  storage.setItem(EXPIRED_KEY, 'true');
}

function onSessionExpired(callback) {
  listeners.push(callback);
  return () => {
    listeners = listeners.filter(listener => listener !== callback);
  };
}

function emitSessionExpired() {
  clearSession();
  markExpiredNotice();
  listeners.forEach(listener => listener());
}

export {
  TOKEN_KEY,
  LOGGED_IN_KEY,
  getToken,
  getIsLoggedIn,
  persistSession,
  clearSession,
  rememberReturnTo,
  takeReturnTo,
  takeExpiredNotice,
  onSessionExpired,
  emitSessionExpired,
};
